/**
 * Notebook doodles — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_doodles`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const notebookDoodlesPack: ToilePack = {
    slug: 'notebook-doodles',
    category: 'coloring',
    name: 'Notebook doodles',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'margin', label: 'Margin doodles', subject: 'a lined notebook page with doodles of stars, arrows, swirls and little faces filling the left margin and the top edge, the lines in the middle left empty'},
        {key: 'cover', label: 'Cover doodles', subject: 'a notebook cover covered edge to edge in doodles of music notes, lightning bolts, planets and hearts around a blank name label'},
        {key: 'grid', label: 'Grid page', subject: 'a square-grid notebook page with small doodles of cubes, spirals, eyes and checkerboards drawn along the grid lines'},
    ],
    styles: [
        {key: 'ballpoint', label: 'Ballpoint', style: 'ballpoint-pen doodles, fine slightly scratchy lines drawn without lifting the hand', palette: 'Blue ink on white paper, pale gray printed paper lines.', framing: 'The notebook page fills the frame, with a thin white border around it.'},
        {key: 'gel', label: 'Gel pen', style: 'black gel-pen doodles, smooth confident lines and small dot shading', palette: 'Black ink on white paper, pale gray printed paper lines.', framing: 'The notebook page fills the frame, with a thin white border around it.'},
        {key: 'highlighter', label: 'Highlighter', style: 'black fineliner doodles with yellow and pink highlighter scribbled behind some of them', palette: 'Black ink with yellow and pink highlighter on white paper, pale gray printed paper lines.', framing: 'The notebook page fills the frame, with a thin white border around it.'},
    ],
    directions: ['space theme', 'food doodles', 'red pen'],
    directionHint: 'try: space theme',
    seo: {
        title: 'Notebook Doodles: Margin, Cover and Grid Pages',
        metaDescription: 'Nine notebook pages of doodles, margin, cover and grid pages, drawn in ballpoint, gel pen and highlighter.',
        h1: 'Notebook doodles: margin doodles, cover doodles, grid page',
        tagline: 'Doodles on a notebook page or cover, drawn in the pens a student carries.',
        intro: 'Notebook doodles are the drawings people make in the margins of class notes and on the cover of a notebook. These nine pages show a notebook page or cover filled with small pen drawings around the edges: margin doodles, a doodled cover, and a grid page. Each is drawn in ballpoint, gel pen or with highlighter. For a different theme, type it in the direction box.',
        useCases: [
            'A notebook cover to copy',
            'Margin doodles for class notes',
            'A grid page for a bullet journal',
            'A page to color in',
        ],
        faq: [
            {q: 'Is the middle of the margin page empty?', a: 'Yes. The doodles fill the left margin and top edge, and the lines in the middle stay empty for notes.'},
            {q: 'What does the highlighter style add?', a: 'Yellow and pink highlighter scribbled behind some of the black fineliner doodles.'},
            {q: 'Can I ask for another theme?', a: 'Yes. Type it in the direction box, such as space theme or food doodles. A typed theme is a request, so the result may differ from the samples.'},
        ],
    },
}
